"use client";

import { projectIdFromProjectDataSource } from "@/lib/project-data-converters/client-cache";
import type {
  AICheckRunHistoryEntry,
  AICheckRunResult,
  ContactTopicsResult,
  FreeformCheckResult,
  OutputLanguage,
  ProjectDataSourceDescriptor,
} from "@/types/aicheck";
import {
  ALL_LANGUAGES,
  CACHE_INVENTORY_KEY,
  CACHE_INVENTORY_VERSION,
  CHECKPOINT_PREFIX,
  CONTACT_TOPICS_PREFIX,
  DISMISSED_PREFIX,
  GRAPH_EXPANDED_PREFIX,
  getCheckpointCacheKey,
  getContactTopicsKey,
  getDismissedKey,
  getGraphExpandedKey,
  getMergeCheckResultCacheKey,
  getProjectDataSourceCacheKey,
  getResultCacheKey,
  HISTORY_KEY,
  MAX_HISTORY,
  MAX_RECENT_EVICTIONS,
  MERGE_CHECK_RESULT_CACHE_VERSION,
  MERGE_CHECK_RESULT_PREFIX,
  PROJECT_DATA_SOURCE_CACHE_VERSION,
  PROJECT_DATA_SOURCE_PREFIX,
  RESULTS_PREFIX,
} from "./cache-keys";
import {
  FREEFORM_RESULT_CACHE_PREFIX,
  FREEFORM_RESULT_CACHE_VERSION,
} from "./freeform-result-cache";

export interface ProjectCacheInventoryEntry {
  projectId: number;
  projectTitle: string;
  lastUpdatedAt: string;
  languages: string[];
  hasResults: boolean;
  hasCheckpoint: boolean;
  hasContactTopics: boolean;
  hasDismissed: boolean;
  hasProjectDataSource: boolean;
  hasMergeCheckResult: boolean;
  hasFreeformReviewResult: boolean;
  sources: ProjectDataSourceDescriptor[];
  totalBytes: number;
}

export interface RecentCacheEviction {
  projectId: number;
  projectTitle: string;
  evictedAt: string;
  freedBytes: number;
}

interface StoredCacheInventory {
  v: number;
  data: {
    entries: ProjectCacheInventoryEntry[];
    recentEvictions: RecentCacheEviction[];
  };
}

interface MutableInventoryEntry extends ProjectCacheInventoryEntry {
  _hasGraphExpanded?: boolean;
}

function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string): void {
  localStorage.setItem(key, value);
}

function removeStorage(key: string): void {
  localStorage.removeItem(key);
}

function parseLanguageKey(
  key: string,
  prefix: string,
): { projectId: number; language: string } | null {
  if (!key.startsWith(prefix)) return null;
  const [projectIdText, language] = key.slice(prefix.length).split(":");
  const projectId = Number.parseInt(projectIdText ?? "", 10);
  if (!Number.isFinite(projectId) || !language) return null;
  return { projectId, language };
}

function parseProjectOnlyKey(key: string, prefix: string): number | null {
  if (!key.startsWith(prefix)) return null;
  const projectId = Number.parseInt(key.slice(prefix.length), 10);
  return Number.isFinite(projectId) ? projectId : null;
}

function measureEntryBytes(key: string, rawValue: string | null): number {
  const value = rawValue ?? "";
  return new Blob([key]).size + new Blob([value]).size;
}

function safeParseJSON<T>(raw: string | null): T | null {
  if (!raw) return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function deriveProjectTitle(
  project: unknown,
  projectId: number,
  fallbackTitle?: string,
): string {
  if (project && typeof project === "object") {
    const record = project as Record<string, unknown> & {
      agency?: { name?: string };
      customer_projects?: Array<{ customer?: { name?: string } }>;
      travelers_count?: number | null;
      tour_code?: number | null;
      name_board_name?: string | null;
      title?: string | null;
      name?: string | null;
      project_name?: string | null;
      tour_name?: string | null;
      itinerary_name?: string | null;
    };
    const directTitle =
      record.name_board_name?.trim() ||
      record.title?.trim() ||
      record.name?.trim() ||
      record.project_name?.trim() ||
      record.tour_name?.trim() ||
      record.itinerary_name?.trim();
    if (directTitle) return directTitle;
    const agencyName = record.agency?.name?.trim() ?? "";
    const customerName =
      record.customer_projects?.[0]?.customer?.name?.trim() ?? "";
    const pax =
      typeof record.travelers_count === "number" && record.travelers_count > 0
        ? `${record.travelers_count}PAX`
        : "";
    const title = [agencyName, customerName, pax].filter(Boolean).join(" ");
    if (title) return title;
    if (typeof record.tour_code === "number")
      return `Project ${record.tour_code}`;
  }
  return fallbackTitle || `Project ${projectId}`;
}

function ensureEntry(
  entries: Map<number, MutableInventoryEntry>,
  projectId: number,
  previous?: ProjectCacheInventoryEntry,
): MutableInventoryEntry {
  const existing = entries.get(projectId);
  if (existing) return existing;
  const created: MutableInventoryEntry = {
    projectId,
    projectTitle: previous?.projectTitle ?? `Project ${projectId}`,
    lastUpdatedAt: previous?.lastUpdatedAt ?? "",
    languages: [],
    hasResults: false,
    hasCheckpoint: false,
    hasContactTopics: false,
    hasDismissed: false,
    hasProjectDataSource: false,
    hasMergeCheckResult: false,
    hasFreeformReviewResult: false,
    sources: [],
    totalBytes: 0,
  };
  entries.set(projectId, created);
  return created;
}

function addSource(
  entry: MutableInventoryEntry,
  source: ProjectDataSourceDescriptor | null | undefined,
): void {
  if (!source?.sourceId) return;
  entry.hasProjectDataSource = true;
  if (!entry.sources.some((item) => item.sourceId === source.sourceId)) {
    entry.sources.push(source);
  }
}

function projectIdFromSource(
  source: ProjectDataSourceDescriptor | undefined,
  fallbackSeed: string,
): number {
  return projectIdFromProjectDataSource(source, fallbackSeed);
}

function hasStableFreeformSourceIdentity(
  source: ProjectDataSourceDescriptor | undefined,
): boolean {
  if (!source?.origin) return false;
  return Boolean(
    source.origin.crmProjectId ||
      source.origin.cacheKey ||
      source.origin.sha256 ||
      source.origin.url ||
      (source.origin.fileName && typeof source.origin.size === "number"),
  );
}

function projectIdFromFreeformCacheData(data: {
  projectId?: number;
  source?: ProjectDataSourceDescriptor;
}): number | null {
  if (!data.projectId) return null;
  if (hasStableFreeformSourceIdentity(data.source)) {
    return projectIdFromSource(data.source, String(data.projectId));
  }
  return data.projectId;
}

function applyTimestamp(
  entry: MutableInventoryEntry,
  candidate: string | null | undefined,
): void {
  if (!candidate) return;
  if (!entry.lastUpdatedAt || candidate > entry.lastUpdatedAt) {
    entry.lastUpdatedAt = candidate;
  }
}

function readStoredInventory(): StoredCacheInventory["data"] {
  const parsed = safeParseJSON<StoredCacheInventory>(
    readStorage(CACHE_INVENTORY_KEY),
  );
  if (!parsed || parsed.v !== CACHE_INVENTORY_VERSION) {
    return { entries: [], recentEvictions: [] };
  }
  return {
    entries: Array.isArray(parsed.data?.entries) ? parsed.data.entries : [],
    recentEvictions: Array.isArray(parsed.data?.recentEvictions)
      ? parsed.data.recentEvictions
      : [],
  };
}

function writeStoredInventory(
  entries: ProjectCacheInventoryEntry[],
  recentEvictions: RecentCacheEviction[],
): void {
  writeStorage(
    CACHE_INVENTORY_KEY,
    JSON.stringify({
      v: CACHE_INVENTORY_VERSION,
      data: {
        entries,
        recentEvictions: recentEvictions.slice(0, MAX_RECENT_EVICTIONS),
      },
    }),
  );
}

export function getHistory(): AICheckRunHistoryEntry[] {
  const parsed = safeParseJSON<AICheckRunHistoryEntry[]>(
    readStorage(HISTORY_KEY),
  );
  return Array.isArray(parsed) ? parsed : [];
}

export function saveHistory(history: AICheckRunHistoryEntry[]): void {
  writeStorage(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

export function upsertHistoryEntry(
  entry: AICheckRunHistoryEntry,
): AICheckRunHistoryEntry[] {
  const filtered = getHistory().filter(
    (item) =>
      !(item.projectId === entry.projectId && item.language === entry.language),
  );
  const updated = [entry, ...filtered].slice(0, MAX_HISTORY);
  saveHistory(updated);
  return updated;
}

export function removeProjectHistoryEntries(
  projectId: number,
): AICheckRunHistoryEntry[] {
  const updated = getHistory().filter((entry) => entry.projectId !== projectId);
  saveHistory(updated);
  return updated;
}

export function removeProjectLanguageHistoryEntry(
  projectId: number,
  language: string,
): AICheckRunHistoryEntry[] {
  const updated = getHistory().filter(
    (entry) => !(entry.projectId === projectId && entry.language === language),
  );
  saveHistory(updated);
  return updated;
}

export function getCacheInventorySnapshot(): {
  entries: ProjectCacheInventoryEntry[];
  recentEvictions: RecentCacheEviction[];
} {
  return readStoredInventory();
}

export function syncProjectCacheInventory(): {
  entries: ProjectCacheInventoryEntry[];
  recentEvictions: RecentCacheEviction[];
} {
  const previous = readStoredInventory();
  const previousById = new Map(
    previous.entries.map((entry) => [entry.projectId, entry]),
  );
  const next = new Map<number, MutableInventoryEntry>();

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || key === HISTORY_KEY || key === CACHE_INVENTORY_KEY) continue;
      const rawValue = readStorage(key);
      const bytes = measureEntryBytes(key, rawValue);

      const resultKey = parseLanguageKey(key, RESULTS_PREFIX);
      if (resultKey) {
        const entry = ensureEntry(
          next,
          resultKey.projectId,
          previousById.get(resultKey.projectId),
        );
        entry.hasResults = true;
        if (!entry.languages.includes(resultKey.language)) {
          entry.languages.push(resultKey.language);
        }
        entry.totalBytes += bytes;
        const parsed = safeParseJSON<{ data?: AICheckRunResult }>(rawValue);
        const result = parsed?.data;
        if (result?.projectData?.project) {
          entry.projectTitle = deriveProjectTitle(
            result.projectData.project,
            resultKey.projectId,
            entry.projectTitle,
          );
        }
        addSource(entry, result?.projectDataSource);
        applyTimestamp(entry, result?.completedAt ?? result?.startedAt);
        continue;
      }

      const checkpointKey = parseLanguageKey(key, CHECKPOINT_PREFIX);
      if (checkpointKey) {
        const entry = ensureEntry(
          next,
          checkpointKey.projectId,
          previousById.get(checkpointKey.projectId),
        );
        entry.hasCheckpoint = true;
        if (!entry.languages.includes(checkpointKey.language)) {
          entry.languages.push(checkpointKey.language);
        }
        entry.totalBytes += bytes;
        const parsed = safeParseJSON<{
          data?: {
            projectDataSnapshot?: { project?: unknown };
            projectDataSource?: ProjectDataSourceDescriptor;
            updatedAt?: string;
          };
        }>(rawValue);
        const checkpoint = parsed?.data;
        if (checkpoint?.projectDataSnapshot?.project) {
          entry.projectTitle = deriveProjectTitle(
            checkpoint.projectDataSnapshot.project,
            checkpointKey.projectId,
            entry.projectTitle,
          );
        }
        addSource(entry, checkpoint?.projectDataSource);
        applyTimestamp(entry, checkpoint?.updatedAt);
        continue;
      }

      const contactTopicsProjectId = parseProjectOnlyKey(
        key,
        CONTACT_TOPICS_PREFIX,
      );
      if (contactTopicsProjectId != null) {
        const entry = ensureEntry(
          next,
          contactTopicsProjectId,
          previousById.get(contactTopicsProjectId),
        );
        entry.hasContactTopics = true;
        entry.totalBytes += bytes;
        const parsed = safeParseJSON<{ data?: ContactTopicsResult }>(rawValue);
        applyTimestamp(entry, parsed?.data?.lastCheckedAt);
        continue;
      }

      const dismissedProjectId = parseProjectOnlyKey(key, DISMISSED_PREFIX);
      if (dismissedProjectId != null) {
        const entry = ensureEntry(
          next,
          dismissedProjectId,
          previousById.get(dismissedProjectId),
        );
        entry.hasDismissed = true;
        entry.totalBytes += bytes;
        continue;
      }

      const graphProjectId = parseProjectOnlyKey(key, GRAPH_EXPANDED_PREFIX);
      if (graphProjectId != null) {
        const entry = ensureEntry(
          next,
          graphProjectId,
          previousById.get(graphProjectId),
        );
        entry._hasGraphExpanded = true;
        entry.totalBytes += bytes;
        continue;
      }

      if (key.startsWith(PROJECT_DATA_SOURCE_PREFIX)) {
        const sourceKey = key.slice(PROJECT_DATA_SOURCE_PREFIX.length);
        const parsed = safeParseJSON<{
          v?: number;
          data?: {
            conversionId?: string;
            source?: ProjectDataSourceDescriptor;
            counts?: {
              itineraryDays?: number;
              itineraryItems?: number;
              arrangements?: number;
            };
            cachedAt?: string;
          };
        }>(rawValue);
        if (
          parsed?.v !== PROJECT_DATA_SOURCE_CACHE_VERSION ||
          !parsed.data?.source
        ) {
          continue;
        }
        const projectId = projectIdFromSource(
          parsed.data.source,
          parsed.data.conversionId ?? sourceKey,
        );
        const entry = ensureEntry(next, projectId, previousById.get(projectId));
        addSource(entry, parsed.data.source);
        entry.totalBytes += bytes;
        entry.projectTitle =
          parsed.data.source.label ||
          entry.projectTitle ||
          `Project ${projectId}`;
        applyTimestamp(entry, parsed.data.cachedAt);
        continue;
      }

      if (key.startsWith(FREEFORM_RESULT_CACHE_PREFIX)) {
        const parsed = safeParseJSON<{
          v?: number;
          data?: {
            projectId?: number;
            language?: OutputLanguage;
            source?: ProjectDataSourceDescriptor;
            templateName?: string;
            stage?: string;
            result?: FreeformCheckResult;
            cachedAt?: string;
          };
        }>(rawValue);
        if (
          parsed?.v !== FREEFORM_RESULT_CACHE_VERSION ||
          !parsed.data?.projectId ||
          !parsed.data?.result
        ) {
          continue;
        }
        const projectId = projectIdFromFreeformCacheData(parsed.data);
        if (!projectId) continue;
        const entry = ensureEntry(next, projectId, previousById.get(projectId));
        entry.hasFreeformReviewResult = true;
        entry.totalBytes += bytes;
        if (
          parsed.data.language &&
          !entry.languages.includes(parsed.data.language)
        ) {
          entry.languages.push(parsed.data.language);
        }
        addSource(entry, parsed.data.source);
        entry.projectTitle =
          parsed.data.source?.label ||
          parsed.data.stage ||
          parsed.data.templateName ||
          entry.projectTitle ||
          `Project ${projectId}`;
        applyTimestamp(
          entry,
          parsed.data.cachedAt ?? parsed.data.result.createdAt,
        );
        continue;
      }

      if (key.startsWith(MERGE_CHECK_RESULT_PREFIX)) {
        const parsed = safeParseJSON<{
          v?: number;
          data?: {
            metadata?: {
              seed?: string;
              sourceLabel?: string;
              baseSource?: ProjectDataSourceDescriptor;
              candidateSource?: ProjectDataSourceDescriptor;
            };
            result?: AICheckRunResult;
            cachedAt?: string;
          };
        }>(rawValue);
        if (
          parsed?.v !== MERGE_CHECK_RESULT_CACHE_VERSION ||
          !parsed.data?.result
        ) {
          continue;
        }
        const projectId = parsed.data.result.projectId;
        const entry = ensureEntry(next, projectId, previousById.get(projectId));
        entry.hasMergeCheckResult = true;
        entry.totalBytes += bytes;
        if (!entry.languages.includes(parsed.data.result.language)) {
          entry.languages.push(parsed.data.result.language);
        }
        if (parsed.data.result.projectData?.project) {
          entry.projectTitle = deriveProjectTitle(
            parsed.data.result.projectData.project,
            projectId,
            parsed.data.metadata?.sourceLabel ?? entry.projectTitle,
          );
        } else {
          entry.projectTitle =
            parsed.data.metadata?.sourceLabel ||
            entry.projectTitle ||
            `Project ${projectId}`;
        }
        addSource(entry, parsed.data.result.projectDataSource);
        addSource(entry, parsed.data.metadata?.baseSource);
        addSource(entry, parsed.data.metadata?.candidateSource);
        applyTimestamp(
          entry,
          parsed.data.cachedAt ??
            parsed.data.result.completedAt ??
            parsed.data.result.startedAt,
        );
      }
    }
  } catch (error) {
    console.warn("[cache-inventory] Failed to scan localStorage", error);
  }

  const entries = [...next.values()]
    .map(({ _hasGraphExpanded: _unused, ...entry }) => ({
      ...entry,
      languages: [...entry.languages].sort(),
    }))
    .sort((a, b) => a.projectId - b.projectId);

  writeStoredInventory(entries, previous.recentEvictions);
  return { entries, recentEvictions: previous.recentEvictions };
}

function getProjectSortAt(
  entry: ProjectCacheInventoryEntry,
  historyRunAtByProject: Map<number, string>,
): string {
  return historyRunAtByProject.get(entry.projectId) ?? entry.lastUpdatedAt;
}

export function getCachedProjects(options?: {
  currentProjectId?: number | null;
}): ProjectCacheInventoryEntry[] {
  const { entries } = syncProjectCacheInventory();
  const historyRunAtByProject = new Map<number, string>();
  for (const historyEntry of getHistory()) {
    const existing = historyRunAtByProject.get(historyEntry.projectId);
    if (!existing || historyEntry.runAt > existing) {
      historyRunAtByProject.set(historyEntry.projectId, historyEntry.runAt);
    }
  }

  return entries
    .filter((entry) => entry.projectId !== options?.currentProjectId)
    .sort((a, b) => {
      const aSortAt = getProjectSortAt(a, historyRunAtByProject);
      const bSortAt = getProjectSortAt(b, historyRunAtByProject);
      if (aSortAt && bSortAt && aSortAt !== bSortAt) {
        return aSortAt.localeCompare(bSortAt);
      }
      if (aSortAt && !bSortAt) return -1;
      if (!aSortAt && bSortAt) return 1;
      return a.projectId - b.projectId;
    });
}

export function getRecentCacheEvictions(): RecentCacheEviction[] {
  return readStoredInventory().recentEvictions;
}

export function getProjectCacheEntry(
  projectId: number,
): ProjectCacheInventoryEntry | null {
  return (
    syncProjectCacheInventory().entries.find(
      (entry) => entry.projectId === projectId,
    ) ?? null
  );
}

export function recordRecentCacheEviction(eviction: RecentCacheEviction): void {
  const snapshot = readStoredInventory();
  writeStoredInventory(snapshot.entries, [
    eviction,
    ...snapshot.recentEvictions,
  ]);
}

export function getProjectSelectionKeys(
  projectId: number,
  options?: {
    results?: boolean;
    checkpoints?: boolean;
    contactTopics?: boolean;
    dismissed?: boolean;
    graphExpanded?: boolean;
    projectDataSources?: boolean;
    mergeCheckResults?: boolean;
    freeformReviewResults?: boolean;
    languages?: OutputLanguage[];
  },
): string[] {
  const selected = {
    results: options?.results ?? false,
    checkpoints: options?.checkpoints ?? false,
    contactTopics: options?.contactTopics ?? false,
    dismissed: options?.dismissed ?? false,
    graphExpanded: options?.graphExpanded ?? false,
    projectDataSources: options?.projectDataSources ?? false,
    mergeCheckResults: options?.mergeCheckResults ?? false,
    freeformReviewResults: options?.freeformReviewResults ?? false,
  };
  const languages =
    options?.languages && options.languages.length > 0
      ? [...new Set(options.languages)]
      : ALL_LANGUAGES;

  const keys: string[] = [];
  if (selected.results) {
    for (const language of languages) {
      keys.push(getResultCacheKey(projectId, language));
    }
  }
  if (selected.checkpoints) {
    for (const language of languages) {
      keys.push(getCheckpointCacheKey(projectId, language));
    }
  }
  if (selected.contactTopics) {
    keys.push(getContactTopicsKey(projectId));
  }
  if (selected.dismissed) {
    keys.push(getDismissedKey(projectId));
  }
  if (selected.graphExpanded) {
    keys.push(getGraphExpandedKey(projectId));
  }
  if (selected.projectDataSources) {
    try {
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!key?.startsWith(PROJECT_DATA_SOURCE_PREFIX)) continue;
        const sourceKey = key.slice(PROJECT_DATA_SOURCE_PREFIX.length);
        const rawValue = readStorage(key);
        const parsed = safeParseJSON<{
          v?: number;
          data?: {
            conversionId?: string;
            source?: ProjectDataSourceDescriptor;
          };
        }>(rawValue);
        if (
          parsed?.v === PROJECT_DATA_SOURCE_CACHE_VERSION &&
          parsed.data?.source &&
          projectIdFromSource(
            parsed.data.source,
            parsed.data.conversionId ?? sourceKey,
          ) === projectId
        ) {
          keys.push(getProjectDataSourceCacheKey(sourceKey));
        }
      }
    } catch {
      // ignore
    }
  }
  if (selected.mergeCheckResults) {
    try {
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!key?.startsWith(MERGE_CHECK_RESULT_PREFIX)) continue;
        const mergeCacheKey = key.slice(MERGE_CHECK_RESULT_PREFIX.length);
        const rawValue = readStorage(key);
        const parsed = safeParseJSON<{
          v?: number;
          data?: {
            result?: AICheckRunResult;
          };
        }>(rawValue);
        if (
          parsed?.v === MERGE_CHECK_RESULT_CACHE_VERSION &&
          parsed.data?.result?.projectId === projectId &&
          (!options?.languages?.length ||
            options.languages.includes(parsed.data.result.language))
        ) {
          keys.push(getMergeCheckResultCacheKey(mergeCacheKey));
        }
      }
    } catch {
      // ignore
    }
  }
  if (selected.freeformReviewResults) {
    try {
      for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (!key?.startsWith(FREEFORM_RESULT_CACHE_PREFIX)) continue;
        const rawValue = readStorage(key);
        const parsed = safeParseJSON<{
          v?: number;
          data?: {
            projectId?: number;
            language?: OutputLanguage;
            source?: ProjectDataSourceDescriptor;
          };
        }>(rawValue);
        const freeformProjectId =
          parsed?.v === FREEFORM_RESULT_CACHE_VERSION && parsed.data
            ? projectIdFromFreeformCacheData(parsed.data)
            : null;
        if (
          freeformProjectId === projectId &&
          (!options?.languages?.length ||
            (parsed?.data?.language &&
              options.languages.includes(parsed.data.language)))
        ) {
          keys.push(key);
        }
      }
    } catch {
      // ignore
    }
  }
  return [...new Set(keys)];
}

export function removeProjectSelectionKeys(
  projectId: number,
  options?: {
    results?: boolean;
    checkpoints?: boolean;
    contactTopics?: boolean;
    dismissed?: boolean;
    graphExpanded?: boolean;
    projectDataSources?: boolean;
    mergeCheckResults?: boolean;
    freeformReviewResults?: boolean;
    languages?: OutputLanguage[];
  },
): number {
  let freedBytes = 0;
  for (const key of getProjectSelectionKeys(projectId, options)) {
    freedBytes += measureEntryBytes(key, readStorage(key));
    removeStorage(key);
  }
  syncProjectCacheInventory();
  return freedBytes;
}

export function removeProjectCacheBundle(projectId: number): {
  freedBytes: number;
  projectTitle: string;
} {
  const snapshot = syncProjectCacheInventory();
  const existing = snapshot.entries.find(
    (entry) => entry.projectId === projectId,
  );
  const freedBytes = removeProjectSelectionKeys(projectId, {
    results: true,
    checkpoints: true,
    contactTopics: true,
    dismissed: true,
    graphExpanded: true,
    projectDataSources: true,
    mergeCheckResults: true,
    freeformReviewResults: true,
  });
  removeProjectHistoryEntries(projectId);
  syncProjectCacheInventory();
  return {
    freedBytes,
    projectTitle: existing?.projectTitle ?? `Project ${projectId}`,
  };
}

export function clearAllAICheckCaches(): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith("aicheck:")) keys.push(key);
    }
    for (const key of keys) {
      removeStorage(key);
    }
  } catch (error) {
    console.warn("[cache-inventory] Failed to clear all caches", error);
  }
}
